/**
 * Empty state shown inside the contract drawer when a player has no
 * uploaded documents yet.
 *
 * Renders a brief description plus a "+ Add Document" call-to-action.
 * The CTA is wired up by the parent {@link ContractDrawerContainer},
 * which switches the drawer mode from `'empty'` to `'upload'`.
 * @module features/contracts/components/ContractEmptyState/ContractEmptyStateView
 */

import { FileText, Plus } from "lucide-react-native";
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Heading } from "@/components/atoms/Heading";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";

/** Props for {@link ContractEmptyStateView}. */
interface Props {
  /** Display name of the player whose drawer is currently open. */
  readonly playerName: string;
  /** Invoked when the user taps "+ Add Document". */
  readonly onAddDocument: () => void;
  /**
   * Whether the current user may trigger the upload mutation.
   * When `false` (the default), the "+ Add Document" button is
   * removed from the DOM entirely so non-OWNER users see no
   * mutation affordance. Defaults to `false` to fail closed — any
   * caller that forgets to plumb the OWNER gate hides the
   * affordance rather than leaking it to non-OWNERs.
   */
  readonly isOwner?: boolean;
}

/**
 * Pure presentation component for the contract drawer empty state.
 * @param props - View props
 * @param props.playerName - Display name of the player whose drawer is open
 * @param props.onAddDocument - Invoked when the user taps "+ Add Document"
 * @param props.isOwner - Whether the CTA should be rendered (Owner-only gate)
 * @returns The rendered empty state with the add-document CTA
 */
const ContractEmptyStateView = ({
  playerName,
  onAddDocument,
  isOwner = false,
}: Props) => (
  <Box testID="contract-empty-state" className="flex-1">
    <Stack className="flex-1 items-center justify-center gap-4 px-6 py-8">
      <Box className="rounded-full bg-surface-subtle p-4">
        <Icon as={FileText} size="xl" className="text-content-muted" />
      </Box>
      <Heading variant="title" className="text-center text-content-primary">
        No contract documents yet
      </Heading>
      <Text variant="body" className="text-center text-content-secondary">
        {`No documents have been uploaded for ${playerName}. Add a contract or transfer agreement to extract key terms.`}
      </Text>
      {isOwner ? (
        <Box className="pt-2">
          <Button
            onPress={onAddDocument}
            testID="contract-empty-state-add-document"
            tone="primary"
            icon={Plus}
            label="Add Document"
          />
        </Box>
      ) : null}
    </Stack>
  </Box>
);

ContractEmptyStateView.displayName = "ContractEmptyStateView";

export default memo(ContractEmptyStateView);
